import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSocketEvent } from './useSocket';

// Short two-tone chime via the Web Audio API — no audio asset to ship.
// Browsers block audio until the user has interacted with the page; an agent
// working the app has always clicked something, so in practice it plays.
let audioCtx = null;
function playChime() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const now = audioCtx.currentTime;
    [[880, 0], [1174.66, 0.12]].forEach(([freq, delay]) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + delay);
      gain.gain.exponentialRampToValueAtTime(0.25, now + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.35);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now + delay);
      osc.stop(now + delay + 0.4);
    });
  } catch { /* audio unsupported or blocked — the badge still shows */ }
}

// Chime + unseen-count badge when a chat is assigned to the current user.
// Reacts only to the workspace 'conversation:assigned' event, whose payload
// carries assigned_to/assigned_by; the bare per-user echo of the same event
// has neither field and falls through the filter, so the assignee's two
// socket events never double-count.
export function useAssignmentAlert(user) {
  const [count, setCount] = useState(0);
  const location = useLocation();
  const onInboxRef = useRef(false);
  onInboxRef.current = location.pathname === '/inbox';

  // Opening the inbox means the assignments have been seen
  useEffect(() => {
    if (location.pathname === '/inbox') setCount(0);
  }, [location.pathname]);

  const onAssigned = useCallback(payload => {
    const assignee = payload?.assigned_to?._id || payload?.assigned_to;
    if (!assignee || String(assignee) !== String(user._id)) return;
    if (payload.assigned_by && String(payload.assigned_by) === String(user._id)) return; // self-assign
    playChime();
    if (!onInboxRef.current) setCount(c => c + 1);
  }, [user._id]);

  useSocketEvent('conversation:assigned', onAssigned);

  return count;
}
